/**
 * usageService.js
 * ===============
 * Cliente do GET /api/me/usage (C3 visao USER).
 *
 * Retorna o consumo de IA do usuario logado no mes corrente + contexto do
 * clube (nome + ai_credits_monthly do plano da subscription).
 *
 * Usado por:
 *   - AccountMenu (rodape do GlobalSidebar) — mostra "X de Y" + nome+clube
 *   - MyUsagePage (/meu-consumo) — pagina completa com breakdown by_agent
 *
 * Cache em memoria: 60s (a cota nao muda a cada render — eh agregado mensal,
 * vale espacar pra nao bombardear o backend num re-render frenetico).
 */
import { AZURE } from './api';

let _cache = null;
let _cacheTs = null;
const CACHE_TTL_MS = 60 * 1000; // 60s

/**
 * Busca o consumo do usuario logado no mes corrente.
 *
 * @param {Object} opts
 * @param {boolean} opts.force - Se true, ignora cache e refaz a chamada.
 * @returns {Promise<{
 *   user: {user_id, name, email, avatar_url, primary_role},
 *   club: {club_id, club_name} | null,
 *   subscription: {subscription_id, plan_key, ai_credits_monthly} | null,
 *   usage_current_month: {
 *     period_start, period_end,
 *     interactions, tokens, cost_usd,
 *     by_agent: [{agent_name, interactions, tokens}, ...]
 *   }
 * }>}
 */
export async function getMyUsage({ force = false } = {}) {
  const now = Date.now();
  if (!force && _cache && _cacheTs && (now - _cacheTs) < CACHE_TTL_MS) {
    return _cache;
  }
  try {
    const { data } = await AZURE.get('/me/usage');
    if (!data?.success) {
      throw new Error(data?.error || 'Falha ao buscar /me/usage');
    }
    _cache = data.data;
    _cacheTs = now;
    return _cache;
  } catch (err) {
    // Nao engasgar a UI — propaga pro caller decidir (mostrar fallback, etc).
    throw err;
  }
}

/**
 * Invalida o cache (chamar apos acoes que alteram consumo, ex: nova chamada
 * de assist via interface).
 */
export function invalidateUsageCache() {
  _cache = null;
  _cacheTs = null;
}

/**
 * Helper de display: formata numero de interacoes com separador de milhar PT-BR.
 * Ex: 21250 -> "21.250"
 */
export function formatInteractions(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('pt-BR');
}

/**
 * Helper de display: formata o "X de Y" do menu.
 * Se nao tem subscription (limite do clube indefinido), mostra so X.
 */
export function formatUsageRatio(usage, subscription) {
  const x = usage?.interactions ?? 0;
  const y = subscription?.ai_credits_monthly;
  if (!y) {
    return `${formatInteractions(x)} interações`;
  }
  return `${formatInteractions(x)} de ${formatInteractions(y)}`;
}
